import { isRecord } from "./parseApiId";

export type JobLogEntry = {
  functionName?: string;
  level?: string;
  line?: number;
  message: string;
  module?: string;
  time?: string;
};

const parseLineNumber = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const parseNestedMessage = (message: string): JobLogEntry | null => {
  const trimmed = message.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    return parseJobLogPayload(JSON.parse(trimmed));
  } catch {
    return null;
  }
};

export const parseJobLogPayload = (value: unknown): JobLogEntry | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return parseJobLogPayload(JSON.parse(trimmed));
    } catch {
      return { message: trimmed };
    }
  }
  if (!isRecord(value)) return null;
  const message = typeof value.message === "string" ? value.message : "";
  const nested = message ? parseNestedMessage(message) : null;
  if (nested) return nested;
  const time =
    typeof value.time === "string" ? value.time : typeof value.timestamp === "string" ? value.timestamp : undefined;
  const level = typeof value.level === "string" ? value.level.toUpperCase() : undefined;
  const moduleName = typeof value.module === "string" ? value.module : undefined;
  const functionName = typeof value.function === "string" ? value.function : undefined;
  const line = parseLineNumber(value.line);
  if (!message && !time && !level && !moduleName && !functionName && line == null) return null;
  return {
    functionName,
    level,
    line,
    message,
    module: moduleName,
    time,
  };
};

export const parseLogStreamLine = (line: string): JobLogEntry | null => {
  const trimmed = line.trim();
  if (!trimmed) return null;
  if (
    trimmed.startsWith(":") ||
    trimmed.startsWith("event:") ||
    trimmed.startsWith("id:") ||
    trimmed.startsWith("retry:")
  ) {
    return null;
  }
  if (trimmed.startsWith("data:")) {
    return parseJobLogPayload(trimmed.slice(5).trimStart());
  }
  return parseJobLogPayload(trimmed);
};

export const splitLogStreamLines = (buffer: string): { lines: string[]; rest: string } => {
  const parts = buffer.split(/\r?\n/);
  const rest = parts.pop() ?? "";
  return { lines: parts, rest };
};

export const emitParsedLogLines = (lines: string[], onLine: (line: JobLogEntry) => void) => {
  lines.forEach((line) => {
    const entry = parseLogStreamLine(line);
    if (entry) onLine(entry);
  });
};
